import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Clock, MessageCircle, Star, DollarSign, X, AlertCircle, Loader2, User, LogOut } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import FundWalletModal from './FundWalletModal';
import WalletTestPanel from './WalletTestPanel';

interface PastReading {
  id: string;
  conversation_id: string;
  advisor_id: string;
  content: string;
  created_at: string;
  advisor?: {
    display_name: string;
    profile_image_url: string | null;
  };
}

interface ActiveChat {
  id: string;
  advisor_id: string;
  client_id: string;
  status: string;
  advisor: {
    display_name: string;
    profile_image_url: string | null;
  };
  updated_at: string;
}

const FUNDING_OPTIONS = [
  { amount: 10, label: '$10' },
  { amount: 25, label: '$25' },
  { amount: 50, label: '$50' }
];

export default function ClientDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const [pastReadings, setPastReadings] = useState<PastReading[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState<string | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<number | null>(null);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [displayName, setDisplayName] = useState<string>('');
  const [activeChats, setActiveChats] = useState<ActiveChat[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [walletChecking, setWalletChecking] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [freeMessagesRemaining, setFreeMessagesRemaining] = useState<number>(0);
  const [freeMessagesUsed, setFreeMessagesUsed] = useState<boolean>(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    checkCurrentUser();
    fetchDashboardData();
    subscribeToChats();
    subscribeToWalletChanges();
  }, [user, navigate]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const paymentSuccess = searchParams.get('payment_success');
    const paymentCancelled = searchParams.get('payment_cancelled');
    const amount = searchParams.get('amount');

    if (paymentSuccess === 'true' && amount) {
      setPaymentSuccess(`Successfully added $${amount} to your wallet!`);
      fetchDashboardData();
      
      navigate('/client/dashboard', { replace: true });
      
      setTimeout(() => {
        setShowBuyCredits(false);
        setPaymentSuccess(null);
      }, 3000);
    } else if (paymentCancelled === 'true') {
      setPaymentError('Payment was cancelled. Please try again.');
      setProcessingPayment(false);
      setSelectedPackage(null);
      
      navigate('/client/dashboard', { replace: true });
    }
  }, [location, navigate]);

  const checkCurrentUser = async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Error getting session:', sessionError);
        return;
      }
      
      if (session?.user) {
        console.log('Current authenticated user:', session.user);
        setUserEmail(session.user.email || '');
      }
    } catch (err) {
      console.error('Error checking current user:', err);
    }
  };

  const subscribeToChats = useCallback(() => {
    if (!user) return;
    
    const channel = supabase.channel('client-chats')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversations',
          filter: `client_id=eq.${user?.id}`
        },
        (payload) => {
          console.log('Conversation update:', payload);
          
          if (payload.new && payload.old && payload.new.status !== payload.old.status) {
            fetchDashboardData();
          }
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
        
        if (status === 'CHANNEL_ERROR' || status === 'CLOSED') {
          console.log('Subscription error, attempting to reconnect...');
          
          setTimeout(() => {
            subscribeToChats();
          }, 2000);
        }
      });

    return () => {
      channel.unsubscribe();
    };
  }, [user]);

  const subscribeToWalletChanges = useCallback(() => {
    if (!user) return;
    
    const walletChannel = supabase.channel('client-wallet')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_wallets',
          filter: `user_id=eq.${user?.id}`
        },
        (payload) => {
          console.log('Wallet update:', payload);
          if (payload.new && typeof payload.new.balance === 'number') {
            setWalletBalance(payload.new.balance);
          }
        }
      )
      .subscribe((status) => {
        console.log('Wallet subscription status:', status);
        
        if (status === 'CHANNEL_ERROR' || status === 'CLOSED') {
          console.log('Wallet subscription error, attempting to reconnect...');
          
          setTimeout(() => {
            subscribeToWalletChanges();
          }, 2000);
        }
      });

    return () => {
      walletChannel.unsubscribe();
    };
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching dashboard data for user:', user?.id);
      
      // Fetch profile data
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', user?.id)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        throw new Error('Failed to load your profile');
      }

      if (profileData) {
        console.log('Profile data:', profileData);
        setDisplayName(profileData.display_name);
      }

      // Fetch wallet balance
      const { data: walletData, error: walletError } = await supabase
        .from('user_wallets')
        .select('balance')
        .eq('user_id', user?.id)
        .maybeSingle();

      if (walletError) {
        console.error('Error fetching wallet:', walletError);
        
        if (walletError.code === 'PGRST116') {
          console.log('No wallet found, creating new wallet...');
          await checkWalletBalance();
        } else {
          throw new Error('Failed to load your wallet balance');
        }
      } else if (walletData) {
        console.log('Wallet data:', walletData);
        setWalletBalance(walletData.balance || 0);
      } else {
        console.log('No wallet data found, creating new wallet...');
        await checkWalletBalance();
      }

      // Fetch free messages info
      const { data: messageCredits, error: creditsError } = await supabase
        .from('message_credits')
        .select('credits_remaining, free_messages_used')
        .eq('user_id', user?.id)
        .single();

      if (creditsError) {
        console.error('Error fetching message credits:', creditsError);
      } else {
        setFreeMessagesRemaining(messageCredits?.credits_remaining || 0);
        setFreeMessagesUsed(messageCredits?.free_messages_used || false);
        console.log('Free messages remaining:', messageCredits?.credits_remaining, 'Used:', messageCredits?.free_messages_used);
      }

      // Fetch past readings (messages from advisors to client)
      const { data: readingsData, error: readingsError } = await supabase
        .from('messages')
        .select(`
          id,
          conversation_id,
          sender_id,
          receiver_id,
          content,
          created_at,
          conversations!inner (
            advisor_id
          ),
          profiles:sender_id (
            display_name,
            profile_image_url
          )
        `)
        .eq('receiver_id', user?.id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (readingsError) {
        console.error('Error fetching past readings:', readingsError);
        throw new Error('Failed to load your reading history');
      }

      console.log('Past readings data:', readingsData);
      
      if (readingsData && readingsData.length > 0) {
        const formattedReadings = readingsData.map(reading => ({
          id: reading.id,
          conversation_id: reading.conversation_id,
          advisor_id: reading.conversations.advisor_id,
          content: reading.content,
          created_at: reading.created_at,
          advisor: reading.profiles
        }));
        
        setPastReadings(formattedReadings);
      } else {
        setPastReadings([]);
      }

      // Fetch active chats
      const { data: chatData, error: chatError } = await supabase
        .from('conversations')
        .select(`
          id,
          advisor_id,
          client_id,
          status,
          updated_at,
          advisor:advisor_id (
            display_name,
            profile_image_url
          )
        `)
        .eq('client_id', user?.id)
        .eq('status', 'active')
        .order('updated_at', { ascending: false });

      if (chatError) {
        console.error('Error fetching active chats:', chatError);
      }

      if (chatData && chatData.length > 0) {
        console.log('Active chats found:', chatData);
        setActiveChats(chatData);
      } else {
        console.log('No active chats found');
        setActiveChats([]);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const checkWalletBalance = async () => {
    if (!user?.id || walletChecking) return;
    
    try {
      setWalletChecking(true);
      console.log('Checking wallet balance via edge function...');
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/check-wallet-balance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          userId: user.id
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to check wallet balance');
      }

      const data = await response.json();
      console.log('Wallet check response:', data);
      
      if (data.wallet) {
        setWalletBalance(data.wallet.balance || 0);
        console.log(`Wallet balance set to ${data.wallet.balance}`);
      }
      
      if (data.created) {
        console.log('New wallet was created');
      }
      
    } catch (err) {
      console.error('Error checking wallet balance:', err);
    } finally {
      setWalletChecking(false);
    }
  };

  const handleBuyCredits = async (amount: number) => {
    setSelectedPackage(amount);
    setProcessingPayment(true);
    setPaymentError(null);
    setPaymentSuccess(null);

    try {
      console.log(`Initiating payment for $${amount}`);
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          amount,
          clientId: user?.id
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to process payment');
      }

      const data = await response.json();
      console.log('Received response:', data);
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned from server');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError(error.message || 'Failed to process payment. Please try again.');
      setProcessingPayment(false);
      setSelectedPackage(null);
    }
  };

  const handleContinueChat = async (chat: ActiveChat) => {
    try {
      setChatLoading(true);
      navigate(`/chat/${chat.id}`);
    } catch (err) {
      console.error('Error continuing chat:', err);
      setError('Failed to continue chat. Please try again.');
    } finally {
      setChatLoading(false);
    }
  };

  const handleViewPastConversation = (conversationId: string) => {
    navigate(`/past-conversation/${conversationId}`);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (err) {
      console.error('Error signing out:', err);
      setError('Failed to sign out. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-purple-900 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 rounded-full bg-purple-800 flex items-center justify-center">
                <User className="w-8 h-8 text-purple-300" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Welcome, {displayName}</h1>
                <p className="text-purple-200">Client Dashboard</p>
                <p className="text-purple-200 text-sm">{userEmail}</p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="bg-purple-700 hover:bg-purple-800 text-white px-4 py-2 rounded-lg flex items-center"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-red-700">{error}</p>
            </div>
            <button 
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {paymentSuccess && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 flex items-center justify-between">
            <div className="flex items-center">
              <Star className="h-5 w-5 text-green-500 mr-2" />
              <p className="text-green-700">{paymentSuccess}</p>
            </div>
            <button 
              onClick={() => setPaymentSuccess(null)}
              className="text-green-500 hover:text-green-700"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
        
        {activeChats.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">Active Chats</h2>
            <div className="space-y-4">
              {activeChats.map((chat) => (
                <div 
                  key={chat.id}
                  className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between"
                >
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center overflow-hidden mr-3">
                      {chat.advisor.profile_image_url ? (
                        <img 
                          src={chat.advisor.profile_image_url} 
                          alt={chat.advisor.display_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-6 h-6 text-green-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{chat.advisor.display_name}</p>
                      <p className="text-sm text-gray-600">
                        Last updated: {new Date(chat.updated_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleContinueChat(chat)}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                    disabled={chatLoading}
                  >
                    {chatLoading ? (
                      <span className="flex items-center">
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </span>
                    ) : (
                      'Continue Chat'
                    )}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Your Credits</h2>
            <button 
              onClick={() => setShowBuyCredits(true)}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Add Funds
            </button>
          </div>
          <div className="flex items-center">
            <DollarSign className="text-purple-600 w-8 h-8 mr-3" />
            <div>
              <p className="text-2xl font-bold">
                ${walletBalance !== undefined ? walletBalance.toFixed(2) : '0.00'}
              </p>
              <p className="text-gray-600">Available balance</p>
            </div>
          </div>
          {freeMessagesRemaining > 0 && !freeMessagesUsed && (
            <div className="mt-4 p-3 bg-green-50 rounded-lg">
              <p className="text-green-700">
                <span className="font-semibold">Good news!</span> You have {freeMessagesRemaining} free {freeMessagesRemaining === 1 ? 'message' : 'messages'} remaining.
              </p>
            </div>
          )}
        </div>

        {showTestPanel && (
          <WalletTestPanel onSuccess={fetchDashboardData} />
        )}

        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-xl font-semibold">Past Readings</h2>
          </div>
          <div className="divide-y">
            {pastReadings && pastReadings.length > 0 ? (
              pastReadings.map((reading) => (
                <div key={reading.id} className="p-6 flex items-center justify-between">
                  <div className="flex items-center">
                    {reading.advisor?.profile_image_url ? (
                      <img
                        src={reading.advisor.profile_image_url}
                        alt={reading.advisor.display_name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <User className="w-6 h-6 text-purple-600" />
                      </div>
                    )}
                    <div className="ml-4">
                      <p className="font-medium">
                        {reading.advisor?.display_name || 'Advisor'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {new Date(reading.created_at).toLocaleDateString()} at {new Date(reading.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <div className="max-w-md">
                    <p className="text-gray-700 line-clamp-2">{reading.content}</p>
                    <button
                      onClick={() => handleViewPastConversation(reading.conversation_id)}
                      className="text-purple-600 text-sm hover:text-purple-800 mt-1"
                    >
                      View Full Conversation
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-6 text-center text-gray-500">
                No readings yet. Start your spiritual journey today!
              </div>
            )}
          </div>
        </div>

        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/advisors')}
            className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors flex items-center mx-auto"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            Find an Advisor
          </button>
        </div>
      </div>

      <FundWalletModal 
        isOpen={showBuyCredits}
        onClose={() => setShowBuyCredits(false)}
        onSuccess={(amount) => {
          setPaymentSuccess(`Adding $${amount} to your wallet...`);
          setTimeout(() => {
            setShowBuyCredits(false);
          }, 2000);
        }}
      />
    </div>
  );
}